/*
  Sale UI style (Product page section / Theme settings → "Sale UI style"). Loaded only when the toggle is on.
  - Ambassador speech bubble: cycles through the messages from settings, one after another, continuously.
  - Ambassador mouse-follow tilt (desktop with a mouse only).
  - Confetti burst once per visit.
  Ambassador markup can be re-rendered on variant change (product-rerender), so everything is looked up live
  or handled by delegated listeners.
*/
(() => {
  const root = document.querySelector('[data-main-product-root].is-sale-ui');
  if (!root) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const AMB = '[data-sale-ambassador]';

  // Garland flags / swags move with SVG (SMIL) animations, which CSS can't stop: pause them for reduced motion,
  // and pause them while scrolled off screen so a full-width SVG isn't repainted every frame for nothing.
  const garlands = document.querySelectorAll('.sale-ribbons__garland');
  if (reduceMotion) {
    garlands.forEach((svg) => svg.pauseAnimations && svg.pauseAnimations());
  } else if (garlands.length && 'IntersectionObserver' in window) {
    const garlandObserver = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!target.pauseAnimations) return;
        if (isIntersecting) target.unpauseAnimations(); else target.pauseAnimations();
      });
    });
    garlands.forEach((svg) => garlandObserver.observe(svg));
  }

  /* ---------- Speech bubble: cycles through the messages in order, continuously ---------- */
  const FIRST_DELAY = 1800; // wait for the photo's rise-in animation
  const SHOW_MS = 3000;     // how long each message stays up
  const GAP_MS = 500;       // pause between messages (bubble hidden)

  const amb = root.querySelector(AMB);
  const messages = amb ? (amb.dataset.messages || '').split('|').map((m) => m.trim()).filter(Boolean) : [];

  if (messages.length) {
    let index = 0;

    const showNext = () => {
      // Look the photo up again each time: a variant change can re-render it.
      const current = root.querySelector(AMB);
      const bubble = current && current.parentElement.querySelector('.sale-ambassador__bubble');
      if (!bubble) { setTimeout(showNext, GAP_MS); return; }

      bubble.textContent = messages[index % messages.length];
      current.classList.add('is-talking');
      index += 1;

      setTimeout(() => {
        current.classList.remove('is-talking');
        setTimeout(showNext, GAP_MS); // loop forever
      }, SHOW_MS);
    };

    setTimeout(showNext, FIRST_DELAY);
  }

  /* ---------- Desktop mouse-follow tilt ---------- */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (finePointer && !reduceMotion) {
    const RANGE = 700; // px from the photo where it still reacts
    let pointer = null;
    let frame = 0;

    const update = () => {
      frame = 0;
      root.querySelectorAll(AMB).forEach((amb) => {
        const tilt = amb.querySelector('.sale-ambassador__tilt');
        if (!tilt) return;
        const rect = amb.getBoundingClientRect();
        if (!pointer || !rect.width) {
          tilt.style.removeProperty('--amb-rx');
          tilt.style.removeProperty('--amb-ry');
          tilt.style.removeProperty('--amb-tx');
          return;
        }
        const dx = pointer.x - (rect.left + rect.width / 2);
        const dy = pointer.y - (rect.top + rect.height / 2);
        const near = Math.hypot(dx, dy) < RANGE;
        const nx = near ? Math.max(-1, Math.min(1, dx / (RANGE / 2))) : 0;
        const ny = near ? Math.max(-1, Math.min(1, dy / (RANGE / 2))) : 0;
        tilt.style.setProperty('--amb-ry', `${(nx * 14).toFixed(2)}deg`);
        tilt.style.setProperty('--amb-rx', `${(-ny * 6).toFixed(2)}deg`);
        tilt.style.setProperty('--amb-tx', `${(nx * 4).toFixed(2)}px`);
      });
    };

    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };

    document.addEventListener('mousemove', (event) => {
      pointer = { x: event.clientX, y: event.clientY };
      schedule();
    }, { passive: true });
    document.documentElement.addEventListener('mouseleave', () => {
      pointer = null;
      schedule();
    });
  }

  /* ---------- Fireworks inside the timer bars ----------
     A canvas in each .sale-gifts layer: rockets rise with a glowing trail, burst into sparks that spread, fall with
     gravity, twinkle and fade. The static SVG bursts stay as the reduced-motion / no-JS fallback.
     The pre-order offer box can be re-rendered on variant change, so new bars are picked up by a periodic rescan. */
  if (!reduceMotion) {
    const PALETTES = [
      ['#ffd36e', '#fff1b8', '#ffb347'], // gold
      ['#ff7ab8', '#ffc2de', '#ff4fa3'], // pink
      ['#7af0ff', '#d4fbff', '#4fc3ff'], // ice blue
      ['#b8ff7a', '#e9ffd0', '#7ae05c'], // lime
      ['#ffffff', '#fff7d6', '#ffe08a'], // white glitter
    ];
    const GRAVITY = 0.035;
    const FRICTION = 0.965;
    const rand = (min, max) => min + Math.random() * (max - min);
    const shows = new Map(); // .sale-gifts element -> show state
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const createShow = (layer) => {
      const canvas = document.createElement('canvas');
      canvas.className = 'sale-gifts__canvas';
      layer.appendChild(canvas);
      layer.classList.add('has-canvas');
      const show = { layer, canvas, ctx: canvas.getContext('2d'), w: 0, h: 0, rockets: [], sparks: [], next: rand(200, 600), visible: true };

      const resize = () => {
        show.w = layer.clientWidth;
        show.h = layer.clientHeight;
        canvas.width = Math.round(show.w * dpr);
        canvas.height = Math.round(show.h * dpr);
        show.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      show.observers = [];
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(resize);
        ro.observe(layer);
        show.observers.push(ro);
      }
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => {
          show.visible = entry.isIntersecting;
          if (show.visible) wake(); // restart the frame loop when a bar scrolls back into view
        });
        io.observe(layer);
        show.observers.push(io);
      }
      return show;
    };

    // xMin/xMax: the slice of the bar this rocket goes up in; wait: ms before it leaves (slight stagger within a volley)
    const launch = (show, xMin, xMax, wait) => {
      const { w, h } = show;
      show.rockets.push({
        wait,
        x: rand(w * xMin, w * xMax),
        y: h + 2,
        vx: rand(-0.25, 0.25),
        vy: -rand(1.6, 2.2) * Math.max(1, h / 45),
        burstY: rand(h * 0.18, h * 0.5),
        palette: PALETTES[Math.floor(Math.random() * PALETTES.length)],
      });
    };

    const explode = (show, rocket) => {
      const count = Math.round(rand(26, 40));
      const power = rand(0.9, 1.5) * Math.max(1, show.h / 50);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + rand(-0.12, 0.12);
        const speed = power * rand(0.55, 1);
        const life = rand(45, 75);
        show.sparks.push({
          x: rocket.x, y: rocket.y,
          vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
          life, max: life,
          size: rand(0.9, 1.6),
          color: rocket.palette[Math.floor(Math.random() * rocket.palette.length)],
          twinkle: Math.random() < 0.4,
        });
      }
      show.flash = { x: rocket.x, y: rocket.y, t: 1, color: rocket.palette[0] };
    };

    const step = (show, k) => {
      const { ctx, w, h } = show;

      // Fade the previous frame instead of clearing it: leaves soft trails behind rockets and sparks.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = `rgba(0,0,0,${Math.min(1, 0.28 * k)})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      show.next -= 16.67 * k;
      if (show.next <= 0) {
        // Volley of 2–3 rockets, each in its own slice of the bar so the bursts sit side by side
        const count = Math.random() < 0.5 ? 2 : 3;
        const slice = 0.88 / count;
        for (let i = 0; i < count; i++) {
          const from = 0.06 + slice * i;
          launch(show, from + slice * 0.1, from + slice * 0.9, i === 0 ? 0 : rand(80, 380));
        }
        show.next = rand(1300, 2000);
      }

      show.rockets = show.rockets.filter((r) => {
        if (r.wait > 0) { r.wait -= 16.67 * k; return true; }
        r.x += r.vx * k;
        r.y += r.vy * k;
        r.vy += GRAVITY * 0.6 * k;
        ctx.fillStyle = '#fff6d6';
        ctx.beginPath(); ctx.arc(r.x, r.y, 1.3, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(255,190,90,.5)';
        ctx.beginPath(); ctx.arc(r.x - r.vx, r.y + 2, 0.9, 0, Math.PI * 2); ctx.fill();
        if (r.y <= r.burstY || r.vy >= 0) { explode(show, r); return false; }
        return true;
      });

      if (show.flash) {
        const f = show.flash;
        const radius = 14 * (1.4 - f.t);
        const glow = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, radius);
        glow.addColorStop(0, `rgba(255,255,255,${0.55 * f.t})`);
        glow.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = glow;
        ctx.beginPath(); ctx.arc(f.x, f.y, radius, 0, Math.PI * 2); ctx.fill();
        f.t -= 0.12 * k;
        if (f.t <= 0) show.flash = null;
      }

      show.sparks = show.sparks.filter((s) => {
        s.vx *= Math.pow(FRICTION, k);
        s.vy = s.vy * Math.pow(FRICTION, k) + GRAVITY * k;
        s.x += s.vx * k;
        s.y += s.vy * k;
        s.life -= k;
        const fade = Math.max(0, s.life / s.max);
        let alpha = fade < 0.6 ? fade / 0.6 : 1;
        if (s.twinkle && fade < 0.5) alpha *= Math.random() < 0.5 ? 0.2 : 1;
        ctx.globalAlpha = alpha * 0.95;
        ctx.fillStyle = s.color;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.size * (0.6 + fade * 0.4), 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        return s.life > 0;
      });
    };

    const scan = () => {
      root.querySelectorAll('.sale-gifts').forEach((layer) => {
        if (!shows.has(layer)) shows.set(layer, createShow(layer));
      });
      shows.forEach((show, layer) => {
        if (layer.isConnected) return;
        show.observers.forEach((o) => o.disconnect());
        shows.delete(layer);
      });
      wake();
    };

    // The frame loop only runs while a bar is on screen and the tab is visible; otherwise it stops completely
    // (no idle requestAnimationFrame ticking) and wake() restarts it.
    let running = false;
    let last = 0;
    const loop = (now) => {
      let active = false;
      if (!document.hidden) {
        const k = last ? Math.min((now - last) / 16.67, 3) : 1; // frame-rate independent, capped after pauses
        shows.forEach((show) => {
          if (show.visible && show.w) { step(show, k); active = true; }
        });
      }
      last = now;
      if (active) {
        requestAnimationFrame(loop);
      } else {
        running = false;
        last = 0;
      }
    };
    function wake() {
      if (running || document.hidden) return;
      running = true;
      requestAnimationFrame(loop);
    }
    document.addEventListener('visibilitychange', wake);

    // Variant changes re-render the pre-order offer box: pick up new bars when the DOM changes,
    // instead of re-scanning the page on a timer.
    let scanQueued = false;
    const queueScan = () => {
      if (scanQueued) return;
      scanQueued = true;
      requestAnimationFrame(() => { scanQueued = false; scan(); });
    };

    // Start after the page has loaded and the browser is idle, so the fireworks never compete with the main image.
    const startFireworks = () => {
      scan();
      new MutationObserver(queueScan).observe(root, { childList: true, subtree: true });
    };
    const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(startFireworks, { timeout: 2000 }) : setTimeout(startFireworks, 200));
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
  }

  /* ---------- Confetti, once per visit ---------- */
  if (reduceMotion || !root.hasAttribute('data-sale-confetti')) return;

  try {
    if (sessionStorage.getItem('saleConfettiShown')) return;
    sessionStorage.setItem('saleConfettiShown', '1');
  } catch (e) { /* storage blocked: still play once for this page */ }

  const colors = ['#6a1bc4', '#9b4dff', '#ffd36e', '#ff7ab8', '#ffffff'];
  const layer = document.createElement('div');
  layer.className = 'sale-confetti';
  layer.setAttribute('aria-hidden', 'true');

  for (let i = 0; i < 60; i++) {
    const piece = document.createElement('span');
    piece.className = 'sale-confetti__piece';
    piece.style.left = `${Math.random() * 100}%`;
    piece.style.background = colors[i % colors.length];
    piece.style.setProperty('--delay', `${(Math.random() * 0.6).toFixed(2)}s`);
    piece.style.setProperty('--dur', `${(1.8 + Math.random() * 1.2).toFixed(2)}s`);
    piece.style.setProperty('--drift', `${Math.round(Math.random() * 160 - 80)}px`);
    piece.style.setProperty('--spin', `${Math.round(Math.random() * 720 - 360)}deg`);
    if (i % 3 === 0) piece.style.borderRadius = '50%';
    layer.appendChild(piece);
  }

  const start = () => {
    document.body.appendChild(layer);
    setTimeout(() => layer.remove(), 3800);
  };

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
